import type { FetchBlotterItemDetailsEpicApi } from 'epic/blotters/commonBlotterEpics';
import type { BlotterItemDetails } from 'models/blotters/BlotterItemDetails';
import { BlotterType } from 'models/blotters/BlotterType';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import type { IHttpClient } from 'utils/http/httpClient.model';

export interface ISearchResponse<T = unknown> {
  entries: readonly T[];
  pageInfo: {
    size: number;
    totalElements: number;
    totalPages: number;
    page: number;
  };
}

export interface ISort {
  column: string;
  direction: string;
}
export interface IBlotterRequest {
  sort?: readonly ISort[];
  filters?: any;
  page?: number;
  size?: number;
  fullTextSearch?: string;
}

export interface ServerSideBlotterSearchRequest {
  externalFilter: ServerSideBlotterExternalFilter;
  fullTextContext?: {
    query: string;
  };
  pageContext: {
    page: number;
    size: number;
    orders: IBlotterOrder[];
  };
  visibilityContext: {
    activityScope?: 'Everything' | 'MyTeams' | 'OnlyMe';
  };
  filters?: IBlotterFilter[];
}

export type ServerSideBlotterExternalFilter = {
  currency?: string;
  startDate?: string;
  endDate?: string;
  platforms?: string;
  product?: string;
  status?: string;
};

export interface IBlotterOrder {
  field: string;
  direction: 'asc' | 'desc';
}

export type IBlotterFilter =
  | IBooleanBlotterFilter
  | IDateBlotterFilter
  | INumberBlotterFilter
  | ITextBlotterFilter
  | ISetBlotterFilter;

export interface IBooleanBlotterFilter {
  field: string;
  type: 'boolean';
  operation: 'equals';
  filter?: ('true' | 'false')[];
}

export interface IDateBlotterFilter {
  field: string;
  type: 'date';
  dateFrom?: string;
  dateTo?: string;
  operation: 'equals' | 'lessThan' | 'greaterThan' | 'inRange' | 'blank' | 'notBlank';
}

export interface INumberBlotterFilter {
  field: string;
  type: 'number';
  filter?: number;
  filterTo?: number;
  operation: 'contains' | 'equals' | 'lessThan' | 'greaterThan' | 'inRange' | 'blank' | 'notBlank';
}

export interface ITextBlotterFilter {
  field: string;
  type: 'text';
  filter?: string;
  operation: 'contains' | 'equals' | 'startsWith' | 'endsWith' | 'blank' | 'notBlank';
}

export interface ISetBlotterFilter {
  field: string;
  type: 'set';
  values?: string[];
  operation: 'equals' | 'contains';
  joinOperator: 'AND' | 'OR';
}

export type BlotterFilterValues = {
  values: Array<{ value: string; count: number }>;
};

export interface IBlotterFilteringApi {
  fetchFilterValues(field: string): Observable<BlotterFilterValues>;
}

export type SearchResponse = ISearchResponse<any>;
export interface IFetchColumnDefinitionEpicApi<Item> {
  fetchColumnDefinition(): Observable<IBlotterColumnDefinition<Item>[]>;
}

export type CommonBlotterApi = FetchBlotterItemDetailsEpicApi;
export function commonBlotterApiConnector(http: IHttpClient): FetchBlotterItemDetailsEpicApi {
  return {
    getItemDetails(
      id: string,
      blotterType: Extract<BlotterType, 'quotes' | 'trades' | 'products' | 'orders' | 'dlpProducts'>,
    ): Observable<BlotterItemDetails> {
      const type = blotterType === 'dlpProducts' ? 'dlp-products' : blotterType;
      return http
        .getJSON<BlotterItemDetails>({
          url: `api/v1/blotter/${type}/${id}/details`,
        })
        .pipe(map(({ payload }) => payload));
    },
    getDlpProductTradesDetails(productId, tradeReference) {
      return http
        .getJSON<BlotterItemDetails>({
          url: `api/v1/blotter/dlp-products/${productId}/trades/${tradeReference}`,
          tryParseErrorPayload: true,
        })
        .pipe(map(({ payload }) => payload));
    },
  };
}
